import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import LockedContent from "@/components/LockedContent";
import { Sparkles, TrendingUp, Map, Calendar } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

interface PremiumInsightsCardProps {
  totalMiles?: number;
  totalRuns?: number;
  activeMonths?: number;
  trailsCompleted?: number;
  isLoading?: boolean;
}

export const PremiumInsightsCard = ({
  totalMiles = 0,
  totalRuns = 0,
  activeMonths = 0,
  trailsCompleted = 0,
  isLoading = false,
}: PremiumInsightsCardProps) => {
  // Generate insights based on user data
  const insights = [
    {
      icon: TrendingUp,
      title: "Activity Trend",
      description: totalRuns > 10 
        ? "You're an active explorer! You log more runs than 75% of the community."
        : totalRuns > 5
        ? "You're building momentum! Keep logging runs to climb the ranks."
        : "Get started! Log a few more runs to see your activity trends.",
    },
    {
      icon: Map,
      title: "Trail Recommendation",
      description: trailsCompleted > 3
        ? "Based on your completed trails, try exploring nearby routes with similar difficulty."
        : "Complete more trails to get personalized recommendations based on your preferences.",
    },
    {
      icon: Calendar,
      title: "Seasonal Insight",
      description: activeMonths >= 6
        ? "You're a year-round adventurer! Your consistency is impressive."
        : activeMonths >= 3
        ? "Great seasonal activity! Consider exploring in different months for variety."
        : "Try to get out more often—even short runs count toward your streak!",
    },
  ];

  const insightsContent = (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          <CardTitle className="text-lg">Premium Insights</CardTitle>
          <Badge variant="secondary" className="text-xs">Coming Soon</Badge>
        </div>
        <CardDescription>
          Personalized recommendations based on your activity
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="w-10 h-10 rounded-lg shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {insights.map((insight, index) => (
              <div key={index} className="flex gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <insight.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-sm">{insight.title}</p>
                  <p className="text-xs text-muted-foreground">{insight.description}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <LockedContent 
      requiredTier="adventurer" 
      featureDescription="Premium Insights - Get personalized trail recommendations and activity trends"
      blurContent
    >
      {insightsContent}
    </LockedContent>
  );
};
